import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";

import { ChartControls } from "../../components/Channel";
import LocationCharts from "../../components/Location/LocationCharts";
import LocationTables from "../../components/Location/LocationTables";
import { useLocationData } from "../../hooks/useLocationData";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import "../../components/Location/Location.css";

// The pickers show Turkey time; the location logs are stamped in UTC.
function turkeyToUtc(date, time) {
  const iso = new Date(`${date}T${time}:00+03:00`).toISOString();
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

export default function ChannelLocation({ channelId, refreshKey, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh }) {
  const today = dayjs().format("YYYY-MM-DD");

  /* -----------------------------
     DATE RANGE
  ------------------------------ */
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  /* -----------------------------
     SELECTED COUNTRY (MAP ONLY)
  ------------------------------ */
  const [selectedCountry, setSelectedCountry] = useState(null);

  /* -----------------------------
     FETCH LOCATION DATA
  ------------------------------ */
  const from = turkeyToUtc(startDate, startTime);
  const to = turkeyToUtc(endDate, endTime);
  const { data, loading, error } = useLocationData({
    channelId,
    startDate: from.date,
    startTime: from.time,
    endDate: to.date,
    endTime: to.time,
    refreshKey,
  });

  /* -----------------------------
     STABLE REFERENCES (FIX ESLINT)
  ------------------------------ */
  const countries = useMemo(() => {
    return data?.countries || [];
  }, [data]);

  const subdivisions = useMemo(() => {
    return data?.subdivisions || [];
  }, [data]);


  /* -----------------------------
     ENSURE VALID SELECTED COUNTRY
  ------------------------------ */
  useEffect(() => {
    if (!countries.length) return;

    const stillExists = countries.some(
      (c) => c.key === selectedCountry
    );

    if (!selectedCountry || !stillExists) {
      const turkey =
        countries.find((c) => c.key === "Turkey") ||
        countries[0];

      setSelectedCountry(turkey.key);
    }
  }, [countries, selectedCountry]);


  const mapSubdivisions = useMemo(() => {
    if (!selectedCountry) return [];
    return subdivisions.filter((c) => c.country === selectedCountry);
  }, [subdivisions, selectedCountry]);

  const filteredSubdivisions = useMemo(() => {
    if (!selectedCountry) return subdivisions;
    return subdivisions.filter((c) => c.country === selectedCountry);
  }, [subdivisions, selectedCountry]);

  /* -----------------------------
     LOGGER INTEGRATION
  ------------------------------ */
  const { logPageChange } = useUserJourneyLogger({
    channelId,
  });
  useEffect(() => {
    logPageChange({
      chartType: "location",
    });
  }, [logPageChange]);

  return (
    <>
      {/* DATE CONTROLS */}
      <ChartControls
        channelId={channelId}
        mode="day"
        hideModeButtons
        startDateInput={startDate}
        endDateInput={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {/* HARD ERRORS */}
      {error && (
        <div style={{ padding: 40, color: "red" }}>
          {error}
        </div>
      )}

      <LocationCharts
        loading={loading}
        countries={countries}
        cities={filteredSubdivisions}      // still named cities INSIDE component
        mapCities={mapSubdivisions}
        selectedCountry={selectedCountry}
        onCountrySelect={setSelectedCountry}
        refreshKey={refreshKey}
      />


      {/* TABLES */}
      <LocationTables
        loading={loading}
        countries={countries}
        cities={subdivisions}
        selectedCountry={selectedCountry}
      />
    </>
  );
}
